import Dialog from "@mui/material/Dialog";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CheckBoxWithLabel } from "../../components/shared/InputField";
import { SearchableSelect } from "../../components/shared/SearchableSelect";
import { ButtonPrimary, ButtonSecondary } from "../../components/utils";
import { useEmployees } from "../../queries/employees";
import { useAddBuybackOffer } from "../../queries/optionBuyback";

interface AddEmployeeToEventDialogProps {
  eventId: string;
  participatingEmployeeIds: Set<string>;
  onClose: () => void;
}

export function AddEmployeeToEventDialog({
  eventId,
  participatingEmployeeIds,
  onClose,
}: AddEmployeeToEventDialogProps) {
  const [employeeId, setEmployeeId] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const { data: allEmployees } = useEmployees();
  const { mutate: addOffer } = useAddBuybackOffer();

  const availableEmployees = useMemo(
    () =>
      (allEmployees ?? []).filter(
        (emp) => !participatingEmployeeIds.has(emp.employeeId ?? emp.id)
      ),
    [allEmployees, participatingEmployeeIds]
  );

  function handleConfirm() {
    if (!employeeId) return;

    addOffer(
      { eventId, employeeId, sendEmail },
      {
        onSuccess: () => {
          toast("Employee added to event successfully", {
            type: "success",
            autoClose: 2000,
          });
          onClose();
        },
        onError: (err: any) => {
          toast(err?.response?.data?.errorRaw || "Failed to add employee", {
            type: "error",
            autoClose: 5000,
          });
        },
      }
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <div className="p-6">
        <p className="text-sm font-semibold text-gray-700 mb-4">
          Add Employee to Event
        </p>
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Employee
            </label>
            <SearchableSelect
              options={availableEmployees}
              value={employeeId}
              valueGetter={(emp) => emp.employeeId ?? emp.id}
              textGetter={(emp) =>
                `${emp.employeeName} (${emp.employeeIdentificationString})`
              }
              placeholder="Search employee"
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </div>
          <CheckBoxWithLabel
            label="Notify employee via email"
            labelclassname="ml-2 text-xs2 text-dark"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
          <ButtonPrimary onClick={handleConfirm}>Add Employee</ButtonPrimary>
        </div>
      </div>
    </Dialog>
  );
}
